import { Alert, Button, Descriptions, Drawer, message, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { adminCommerceApi } from '@/services/adminCommerceApi';
import { authStore } from '@/stores';
import type {
  AdminCommerceMembershipDetail,
  AdminCommerceOrderDetail,
} from '@/types';
import { MembershipDrawer } from './MembershipDrawer';
import { RefundDialog } from './RefundDialog';

interface OrderDetailDrawerProps {
  open: boolean;
  loading: boolean;
  brandId: string;
  detail: AdminCommerceOrderDetail | null;
  onClose: () => void;
  onRefresh: () => void;
}

function displayTime(value: string) {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

function money(value: number, currency: string) {
  const normalized = currency.toUpperCase();
  const exponent = ['JPY', 'KRW'].includes(normalized)
    ? 0
    : ['BHD', 'JOD', 'KWD'].includes(normalized)
      ? 3
      : 2;
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(value / 10 ** exponent);
}

export function OrderDetailDrawer({
  open,
  loading,
  brandId,
  detail,
  onClose,
  onRefresh,
}: OrderDetailDrawerProps) {
  const [refundOpen, setRefundOpen] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipDetail, setMembershipDetail] =
    useState<AdminCommerceMembershipDetail | null>(null);
  const summary = detail?.summary;
  const canRefund = authStore.can(ADMIN_PERMISSIONS.commerceOrdersRefund);

  useEffect(() => {
    const refund = detail?.refund;
    if (
      !open ||
      !summary ||
      refund?.status !== 'processing' ||
      !refund.refundRequestNo
    ) {
      return;
    }
    let cancelled = false;
    const startedAt = Date.now();
    const poll = async () => {
      while (!cancelled && Date.now() - startedAt < 60_000) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        if (cancelled) {
          return;
        }
        try {
          const status = await adminCommerceApi.getRefund(
            brandId,
            summary.orderNo,
            refund.refundRequestNo,
          );
          if (status.status !== 'processing' && status.status !== 'pending') {
            onRefresh();
            return;
          }
        } catch {
          // A transient query error must not stop status recovery.
        }
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [brandId, detail?.refund, onRefresh, open, summary]);

  const openMembership = async () => {
    if (!summary) {
      return;
    }
    setMembershipOpen(true);
    setMembershipLoading(true);
    try {
      setMembershipDetail(
        await adminCommerceApi.getMembership(brandId, summary.userId),
      );
    } catch {
      message.error('会员周期加载失败');
    } finally {
      setMembershipLoading(false);
    }
  };

  return (
    <>
      <Drawer
        title="订单详情"
        width={820}
        open={open}
        loading={loading}
        onClose={onClose}
        extra={
          <Space>
            {summary?.productType === 'membership_subscription' && (
              <Button onClick={() => void openMembership()}>
                查看会员周期
              </Button>
            )}
            <Button
              danger
              type="primary"
              disabled={
                !canRefund ||
                !detail?.refundPreview ||
                detail.refundPreview.maxRefundAmount <= 0 ||
                Boolean(detail.refund && !detail.refund.retryable)
              }
              onClick={() => setRefundOpen(true)}
            >
              退款
            </Button>
          </Space>
        }
      >
        {summary && (
          <>
            {detail?.refund?.status === 'manual_review' && (
              <Alert
                className="mb-4"
                type="error"
                showIcon
                message="退款已完成、权益处理异常"
                description={detail.refund.message}
              />
            )}
            {detail?.refund?.status === 'processing' && (
              <Alert
                className="mb-4"
                type="info"
                showIcon
                message="退款仍在处理中"
                description="重新打开订单会继续查询最新状态，请勿重复发起退款。"
              />
            )}
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="订单号">
                {summary.orderNo}
              </Descriptions.Item>
              <Descriptions.Item label="用户 ID">
                {summary.userId}
              </Descriptions.Item>
              <Descriptions.Item label="订单类型">
                {summary.orderType}
              </Descriptions.Item>
              <Descriptions.Item label="商品类型">
                {summary.productType}
              </Descriptions.Item>
              <Descriptions.Item label="SKU">
                {summary.skuCode}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag>{summary.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="历史实付">
                {money(summary.amountTotal, summary.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="已退款">
                {money(summary.amountRefunded, summary.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="订单积分">
                {summary.creditsAmount.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="支付时间">
                {displayTime(summary.paidAt)}
              </Descriptions.Item>
              <Descriptions.Item label="PaymentIntent">
                {summary.stripePaymentIntentId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Subscription">
                {summary.stripeSubscriptionId || '-'}
              </Descriptions.Item>
            </Descriptions>

            {detail?.refundPreview && (
              <>
                <h3 className="mb-3 mt-6 text-base font-semibold">
                  退款影响预览
                </h3>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="系统建议金额">
                    {money(
                      detail.refundPreview.suggestedRefundAmount,
                      summary.currency,
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="最大可退金额">
                    {money(
                      detail.refundPreview.maxRefundAmount,
                      summary.currency,
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="订单总权益积分">
                    {detail.refundPreview.totalEntitlementCredits.toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="有效剩余积分">
                    {detail.refundPreview.validRemainingCredits.toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="未来未发积分">
                    {detail.refundPreview.futureUnissuedCredits.toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="影响当前会员">
                    {detail.refundPreview.affectsCurrentMembership
                      ? '立即终止会员并取消对应订阅'
                      : '不影响当前会员'}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {detail?.refund && (
              <>
                <h3 className="mb-3 mt-6 text-base font-semibold">退款记录</h3>
                {detail.refund.runtimeShortfallCredits > 0 && (
                  <Alert
                    className="mb-3"
                    type="warning"
                    showIcon
                    message={`实际少回收 ${detail.refund.runtimeShortfallCredits.toLocaleString()} 积分`}
                  />
                )}
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="退款状态">
                    {detail.refund.status}
                  </Descriptions.Item>
                  <Descriptions.Item label="Stripe Refund">
                    {detail.refund.stripeRefundId || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="系统建议">
                    {money(
                      detail.refund.calculatedRefundAmount,
                      detail.refund.currency || summary.currency,
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="客服最终退款">
                    {money(
                      detail.refund.approvedRefundAmount,
                      detail.refund.currency || summary.currency,
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="首次申请时间">
                    {displayTime(detail.refund.applicationReceivedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="操作人 UID">
                    {detail.refund.operatorUid || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="退款原因">
                    {detail.refund.reason || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="金额调整原因">
                    {detail.refund.overrideReason || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="退款对应积分">
                    {detail.refund.refundEquivalentCredits.toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="实际少回收积分">
                    {detail.refund.runtimeShortfallCredits.toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="更新时间">
                    {displayTime(detail.refund.updatedAt)}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </>
        )}
      </Drawer>

      {summary && (
        <RefundDialog
          open={refundOpen}
          brandId={brandId}
          orderNo={summary.orderNo}
          existingRefund={detail?.refund?.retryable ? detail.refund : undefined}
          initialPreview={detail?.refundPreview ?? null}
          onClose={() => setRefundOpen(false)}
          onCompleted={onRefresh}
        />
      )}

      <MembershipDrawer
        open={membershipOpen}
        loading={membershipLoading}
        detail={membershipDetail}
        onClose={() => setMembershipOpen(false)}
      />
    </>
  );
}
